import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { DEMO_WORKSPACE_ID, demoBoards, demoCards, demoColumns, demoProfiles, demoVideos, legalQuestions, workflows } from "../data/demo";
import i18n from "../lib/i18n";
import { isDemoMode } from "../lib/supabase";
import { archiveBoard, archiveCardRecord, archiveColumn, archiveVideoRecord, insertBoard, insertCard, insertColumn, insertVideo, loadWorkspaceSnapshot, persistCard, persistColumnName, persistVideoStatus, type WorkspaceSnapshot } from "../lib/workspaceRepository";
import { createVideoRecord, nextVideoStatus, videoStatusKey, type VideoStatusKey } from "../lib/videoWorkflow";
import { useAuth } from "./AuthContext";
import type { Accent, Board, BoardColumn, Card, SearchResult, VideoRecord, WorkspaceProfile } from "../types/domain";

interface WorkspaceValue {
  boards: Board[];
  columns: BoardColumn[];
  cards: Card[];
  videos: VideoRecord[];
  profiles: WorkspaceProfile[];
  loading: boolean;
  refreshing: boolean;
  syncError: string | null;
  refresh(): Promise<void>;
  activeBoardId: string;
  setActiveBoardId(id: string): void;
  addBoard(title: string, description: string, theme?: Accent): Board;
  deleteBoard(boardId: string): void;
  addCard(columnId: string, title: string): void;
  archiveCard(cardId: string): void;
  updateCard(card: Card): void;
  moveCard(cardId: string, columnId: string, position?: number): void;
  addColumn(boardId: string, title: string, accent?: Accent): void;
  renameColumn(columnId: string, title: string): void;
  deleteColumn(columnId: string): void;
  createVideo(input: { title: string; audience: string; status: VideoStatusKey; goal: string; pillar: string }): VideoRecord;
  duplicateVideo(videoId: string, suffix: string): VideoRecord | null;
  archiveVideo(videoId: string): void;
  advanceVideo(videoId: string): void;
  search(query: string): SearchResult[];
}

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [boards, setBoards] = usePersistedState("pactios-demo-boards", isDemoMode ? demoBoards : [], isDemoMode);
  const [columns, setColumns] = usePersistedState("pactios-demo-columns", isDemoMode ? demoColumns : [], isDemoMode);
  const [cards, setCards] = usePersistedState("pactios-demo-cards", isDemoMode ? demoCards : [], isDemoMode);
  const [videos, setVideos] = useState(isDemoMode ? demoVideos : []);
  const [profiles, setProfiles] = useState(isDemoMode ? demoProfiles : []);
  const [activeBoardId, setActiveBoardId] = useState(isDemoMode ? "operations" : "");
  const [loading, setLoading] = useState(!isDemoMode);
  const [refreshing, setRefreshing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const lastRefreshAt = useRef(0);

  const applySnapshot = useCallback((snapshot: WorkspaceSnapshot) => {
    setBoards(snapshot.boards);
    setColumns(snapshot.columns);
    setCards(snapshot.cards);
    setVideos(snapshot.videos);
    setProfiles(snapshot.profiles);
    setActiveBoardId((current) => current || snapshot.boards[0]?.id || "");
    setSyncError(null);
    lastRefreshAt.current = Date.now();
  }, [setBoards, setCards, setColumns]);

  const refresh = useCallback(async () => {
    if (isDemoMode || !profile) return;
    setRefreshing(true);
    try {
      applySnapshot(await loadWorkspaceSnapshot());
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : i18n.t("common.loadError"));
    } finally {
      setRefreshing(false);
    }
  }, [applySnapshot, profile]);

  useEffect(() => {
    if (isDemoMode || !profile) return;
    let active = true;
    setLoading(true);
    void loadWorkspaceSnapshot()
      .then((snapshot) => { if (active) applySnapshot(snapshot); })
      .catch((error: unknown) => active && setSyncError(error instanceof Error ? error.message : i18n.t("common.loadError")))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [applySnapshot, profile]);

  // Three administrators share this workspace without a realtime channel, so a
  // returning tab re-reads the snapshot rather than trusting its local copy.
  useEffect(() => {
    if (isDemoMode || !profile) return;
    const refreshOnReturn = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefreshAt.current < 30_000) return;
      void refresh();
    };
    window.addEventListener("focus", refreshOnReturn);
    document.addEventListener("visibilitychange", refreshOnReturn);
    return () => {
      window.removeEventListener("focus", refreshOnReturn);
      document.removeEventListener("visibilitychange", refreshOnReturn);
    };
  }, [profile, refresh]);

  const reportSyncError = useCallback((error: unknown) => setSyncError(error instanceof Error ? error.message : i18n.t("common.writeError")), []);

  const addBoard = useCallback((title: string, description: string, theme: Accent = "cobalt") => {
    const next: Board = {
      id: crypto.randomUUID(),
      title: title.trim(),
      description: description.trim(),
      theme,
      position: boards.length ? Math.max(...boards.map((board) => board.position)) + 1 : 0,
      archivedAt: null,
    };
    setBoards((current) => [...current, next]);
    if (!isDemoMode) void insertBoard(next, DEMO_WORKSPACE_ID).catch(reportSyncError);
    return next;
  }, [boards, reportSyncError]);

  const deleteBoard = useCallback((boardId: string) => {
    if (cards.some((card) => card.boardId === boardId && !card.archivedAt)) return;
    setBoards((current) => current.filter((board) => board.id !== boardId));
    if (!isDemoMode) void archiveBoard(boardId).catch(reportSyncError);
  }, [cards, reportSyncError, setBoards]);

  const archiveCard = useCallback((cardId: string) => {
    setCards((current) => current.map((card) => card.id === cardId ? { ...card, archivedAt: new Date().toISOString() } : card));
    if (!isDemoMode) void archiveCardRecord(cardId).catch(reportSyncError);
  }, [reportSyncError, setCards]);

  const addCard = useCallback((columnId: string, title: string) => {
    const targetColumn = columns.find((column) => column.id === columnId);
    if (!targetColumn) return;
    const columnCards = cards.filter((card) => card.columnId === columnId);
    const next: Card = {
      id: crypto.randomUUID(),
      boardId: targetColumn.boardId,
      columnId,
      title: title.trim(),
      description: "",
      accent: columns.find((column) => column.id === columnId)?.accent ?? "cobalt",
      category: "Операции",
      assigneeIds: profile ? [profile.id] : [],
      labels: [],
      startDate: null,
      dueDate: null,
      position: columnCards.length,
      checklists: [],
      comments: [],
      archivedAt: null,
      updatedAt: new Date().toISOString(),
      revision: 1,
    };
    setCards((current) => [...current, next]);
    if (!isDemoMode) void insertCard(next, DEMO_WORKSPACE_ID).catch(reportSyncError);
  }, [cards, columns, profile, reportSyncError, setCards]);

  const updateCard = useCallback((nextCard: Card) => {
    const existing = cards.find((card) => card.id === nextCard.id);
    if (!existing) return;
    const updated: Card = { ...nextCard, revision: existing.revision + 1, updatedAt: new Date().toISOString() };
    setCards((current) => current.map((card) => card.id === updated.id ? updated : card));
    if (!isDemoMode) void persistCard(updated).catch(reportSyncError);
  }, [cards, reportSyncError, setCards]);

  const moveCard = useCallback((cardId: string, columnId: string, position?: number) => {
    const existing = cards.find((card) => card.id === cardId);
    if (!existing) return;
    if (existing.columnId === columnId && position === undefined) return;
    const target = position ?? cards.filter((card) => card.columnId === columnId && card.id !== cardId).length;
    const updated: Card = { ...existing, columnId, position: target, updatedAt: new Date().toISOString(), revision: existing.revision + 1 };
    setCards((current) => current.map((card) => card.id === cardId ? updated : card));
    if (!isDemoMode) void persistCard(updated).catch(reportSyncError);
  }, [cards, reportSyncError, setCards]);

  const addColumn = useCallback((boardId: string, title: string, accent: Accent = "graphite") => {
    const next: BoardColumn = {
      id: crypto.randomUUID(),
      boardId,
      title: title.trim(),
      accent,
      position: columns.filter((column) => column.boardId === boardId).length,
      archivedAt: null,
    };
    setColumns((current) => [...current, next]);
    if (!isDemoMode) void insertColumn(next, DEMO_WORKSPACE_ID).catch(reportSyncError);
  }, [columns, reportSyncError, setColumns]);

  const renameColumn = useCallback((columnId: string, title: string) => {
    setColumns((current) => current.map((column) => column.id === columnId ? { ...column, title: title.trim() } : column));
    if (!isDemoMode) void persistColumnName(columnId, title.trim()).catch(reportSyncError);
  }, [reportSyncError, setColumns]);

  const deleteColumn = useCallback((columnId: string) => {
    if (cards.some((card) => card.columnId === columnId && !card.archivedAt)) return;
    setColumns((current) => current.filter((column) => column.id !== columnId));
    if (!isDemoMode) void archiveColumn(columnId).catch(reportSyncError);
  }, [cards, reportSyncError, setColumns]);

  const createVideo = useCallback((input: { title: string; audience: string; status: VideoStatusKey; goal: string; pillar: string }) => {
    const next = createVideoRecord(input);
    setVideos((current) => [next, ...current]);
    if (!isDemoMode) void insertVideo(next, DEMO_WORKSPACE_ID).catch(reportSyncError);
    return next;
  }, [reportSyncError]);

  const duplicateVideo = useCallback((videoId: string, suffix: string) => {
    const source = videos.find((video) => video.id === videoId);
    if (!source) return null;
    const now = new Date().toISOString();
    const next: VideoRecord = { ...source, id: crypto.randomUUID(), title: `${source.title} ${suffix}`, post_url: "", publish_date: null, created_at: now, updated_at: now };
    setVideos((current) => [next, ...current]);
    if (!isDemoMode) void insertVideo(next, DEMO_WORKSPACE_ID).catch(reportSyncError);
    return next;
  }, [reportSyncError, videos]);

  const archiveVideo = useCallback((videoId: string) => {
    setVideos((current) => current.filter((video) => video.id !== videoId));
    if (!isDemoMode) void archiveVideoRecord(videoId).catch(reportSyncError);
  }, [reportSyncError]);

  const advanceVideo = useCallback((videoId: string) => {
    const existing = videos.find((video) => video.id === videoId);
    if (!existing) return;
    const status = nextVideoStatus(existing.status);
    setVideos((current) => current.map((video) => video.id === videoId ? { ...video, status, updated_at: new Date().toISOString() } : video));
    if (!isDemoMode) void persistVideoStatus(videoId, status).catch(reportSyncError);
  }, [reportSyncError, videos]);

  const search = useCallback((rawQuery: string): SearchResult[] => {
    const query = normalize(rawQuery);
    if (query.length < 2) return [];
    const contains = (value: unknown) => normalize(String(value ?? "")).includes(query);
    const score = (title: string, body: string) => normalize(title).startsWith(query) ? 100 : contains(title) ? 80 : contains(body) ? 50 : 0;

    const cardResults = cards.flatMap<SearchResult>((card) => {
      const body = [card.description, card.category, ...card.labels, ...card.checklists.flatMap((list) => list.items.map((item) => item.title))].join(" ");
      const resultScore = score(card.title, body);
      if (!resultScore) return [];
      const column = columns.find((item) => item.id === card.columnId);
      const board = boards.find((item) => item.id === card.boardId);
      return [{ id: card.id, kind: "card", title: card.title, excerpt: card.description || card.category, location: `${board?.title ?? i18n.t("nav.boards")} · ${column?.title ?? i18n.t("workspace.column")}`, route: `/boards/${card.boardId}?card=${card.id}`, score: resultScore }];
    });
    const videoResults = videos.flatMap<SearchResult>((video) => {
      const body = [video.script, video.pain, video.goal, video.pillar, video.cta, video.caption].join(" ");
      const resultScore = score(video.title, body);
      return resultScore ? [{ id: video.id, kind: "video", title: video.title, excerpt: video.goal || video.pillar, location: `${i18n.t("nav.videos")} · ${i18n.t(`videos.statuses.${videoStatusKey(video.status)}`)}`, route: `/videos?video=${video.id}`, score: resultScore }] : [];
    });
    const workflowResults = workflows.flatMap<SearchResult>((workflow) => {
      const resultScore = score(`${workflow.titleBg} ${workflow.titleEn}`, `${workflow.descriptionBg} ${workflow.descriptionEn} ${workflow.pageName}`);
      const english = i18n.resolvedLanguage === "en";
      return resultScore ? [{ id: workflow.id, kind: "workflow", title: english ? workflow.titleEn : workflow.titleBg, excerpt: english ? workflow.descriptionEn : workflow.descriptionBg, location: `${i18n.t("nav.workflows")} · ${workflow.order}/8`, route: `/workflows/${workflow.id}`, score: resultScore }] : [];
    });
    const legalResults = legalQuestions.flatMap<SearchResult>((question) => {
      const resultScore = score(question.prompt, `${question.help} ${question.options?.join(" ") ?? ""}`);
      return resultScore ? [{ id: question.id, kind: "legal", title: question.prompt, excerpt: question.help, location: `${i18n.t("nav.legal")} · ${i18n.t("legal.questionnaire")}`, route: `/legal?question=${question.id}`, score: resultScore }] : [];
    });
    return [...cardResults, ...videoResults, ...workflowResults, ...legalResults]
      .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title, i18n.resolvedLanguage === "en" ? "en" : "bg"))
      .slice(0, 40);
  }, [boards, cards, columns, videos]);

  const value = useMemo<WorkspaceValue>(() => ({
    boards, columns, cards, videos, profiles, loading, refreshing, syncError, refresh, activeBoardId, setActiveBoardId,
    addBoard, deleteBoard, addCard, archiveCard, updateCard, moveCard, addColumn, renameColumn, deleteColumn, createVideo, duplicateVideo, archiveVideo, advanceVideo, search,
  }), [activeBoardId, addBoard, addCard, addColumn, advanceVideo, archiveCard, archiveVideo, boards, cards, columns, createVideo, deleteBoard, deleteColumn, duplicateVideo, loading, moveCard, profiles, refresh, refreshing, renameColumn, search, syncError, updateCard, videos]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

function normalize(value: string) {
  return value.toLocaleLowerCase("bg").normalize("NFKD").trim();
}

// Only the self-contained demo keeps its board state in the browser. Connected
// workspaces are authoritative in Supabase and must not read a stale local copy.
function usePersistedState<T>(key: string, initialValue: T, enabled: boolean) {
  const [value, setValue] = useState<T>(() => {
    if (!enabled) return initialValue;
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) as T : initialValue;
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    if (!enabled) return;
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
  }, [enabled, key, value]);
  return [value, setValue] as const;
}

export function useWorkspace() {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("useWorkspace must be used inside WorkspaceProvider");
  return value;
}
